import { Alert, Box, CircularProgress, Stack } from "@mui/material";
import { captureException } from "@sentry/react";
import { type FC, useCallback } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import { useDialog } from "../../../../utils/hooks/use-dialog";
import { useFlag } from "../../../../server/optimizely";
import { UnsavedIndicator } from "../../../generic/components/unsaved-indicator";
import { UniqueSegmentNameError } from "../../../segment/server/segment-state-service";
import type { MomentRestrictions } from "../../hooks/use-moment-restrictions";
import type { MomentFormMoment } from "../../types/moment-form-moment";
import { didPersistMoment, useHandleSave } from "./hooks/useHandleSave";
import type { HandleSaveMoment } from "./hooks/types";
import { PreviewButton } from "./preview-button/preview-button";
import { PublishButton } from "./publish-button";
import { SaveDraftButton } from "./save-draft-button";
import { SendForReviewButton } from "./send-for-review/send-for-review-button";
import { SendTestButton } from "./send-test/send-test-button";
import type { SaveButtonProps } from "./types";

export const MomentFormButtons: FC<
  Omit<SaveButtonProps, "handleSave"> & {
    momentRestrictions: MomentRestrictions;
    isNewsletter?: boolean;
    onSaved: () => void;
  }
> = ({ momentRestrictions, isNewsletter, formSaveState, saveDataReady, onSaved, ...props }) => {
  const [reviewExperienceEnabled] = useFlag("reviewer_role");
  const {
    control,
    getValues,
    reset,
    trigger,
    setError,
    formState: { defaultValues },
  } = useFormContext<MomentFormMoment>();

  const previewDialog = useDialog<{ isReview: boolean }>();

  const { isDirty } = useFormState();

  useWatch({
    control,
    name: ["running", "unrecoverable_error", "id"],
  });
  const [running, unrecoverable_error, momentId] = getValues([
    "running",
    "unrecoverable_error",
    "id",
  ]);

  const { callback: saveMoment, loading } = useHandleSave({
    reset: (vals) => reset({ ...defaultValues, ...vals }, { keepValues: true, keepErrors: true }),
    validate: trigger,
    errorHandlers: {
      segment: (err: Error) => {
        if (err instanceof UniqueSegmentNameError) {
          setError("segment.name", { message: "Segment name already exists" });
        } else {
          captureException(err);
        }
      },
      design: (err: Error) => setError("design", { message: err.message }),
    },
    refreshCoverImage: (url) => defaultValues?.cover_image_path !== url,
  });

  const handleSave = useCallback(
    async (values: HandleSaveMoment) => {
      const result = await saveMoment(values);
      if (didPersistMoment(result)) onSaved();
      return result;
    },
    [onSaved, saveMoment],
  );

  if (loading || !momentId)
    return (
      <Stack direction="row" spacing={2} sx={{ py: 2 }}>
        <CircularProgress />
      </Stack>
    );
  return (
    <Stack direction="row" spacing={2} sx={{ py: 2 }}>
      {running && <Alert severity="success">Communication published</Alert>}
      <Box sx={{ ml: "auto" }}></Box>
      {unrecoverable_error ? (
        <Alert severity="error">Communication Corrupt, please contact support.</Alert>
      ) : (
        <>
          <UnsavedIndicator isDirty={isDirty} />
          <SendForReviewButton
            {...props}
            handleSave={handleSave}
            formSaveState={formSaveState}
            saveDataReady={saveDataReady}
          />
          <PreviewButton
            {...props}
            isNewsletter={isNewsletter}
            handleSave={handleSave}
            formSaveState={formSaveState}
            saveDataReady={saveDataReady}
            momentId={momentId}
            previewDialog={previewDialog}
            showButton={!reviewExperienceEnabled}
          />
          {reviewExperienceEnabled && (
            <SaveDraftButton
              {...props}
              handleSave={handleSave}
              formSaveState={formSaveState}
              saveDataReady={saveDataReady}
            />
          )}
          <SendTestButton
            {...props}
            formSaveState={formSaveState}
            handleSave={handleSave}
            saveDataReady={saveDataReady}
          />
          {!reviewExperienceEnabled && (
            <SaveDraftButton
              {...props}
              handleSave={handleSave}
              formSaveState={formSaveState}
              saveDataReady={saveDataReady}
            />
          )}
          <PublishButton
            {...props}
            formSaveState={formSaveState}
            momentRestrictions={momentRestrictions}
            handleSave={handleSave}
            saveDataReady={saveDataReady}
            review={{ value: true, open: previewDialog.handleOpen }}
            closePreviewDialog={previewDialog.handleClose}
          />
        </>
      )}
    </Stack>
  );
};
